import React from 'react';

import SummarizeIcon from '@atlaskit/icon/core/summarize';
import { AIEventsInstrumentationProvider } from '@atlassian/ai-analytics';

import {
  AiAssistant,
  customAnalyticsFire,
} from 'src/components/pipelines/components/ai-assistant';
import { ChangesState } from 'src/components/pipelines/redux/reducers/changes';
import { useIntl } from 'src/hooks/intl';

import { messages } from './DeploymentSummaryAiAssistant.i18n';

type Props = {
  commits: ChangesState['commitDiff'];
  issues: ChangesState['jiraIssues'];
  pullRequests: ChangesState['pullRequests'];
  portalId: string;
  repositoryUuid: string;
};

const AI_PROMPT_KEY = 'DEPLOYMENT_SUMMARY';

export const DeploymentSummaryAiAssistant = ({
  commits,
  issues,
  pullRequests,
  portalId,
  repositoryUuid,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();

  const getContext = () =>
    JSON.stringify({
      pullRequests: pullRequests.map(pr => ({
        author: pr.author.display_name,
        id: pr.id,
        title: pr.title,
        url: pr['links.html.href'],
        sourceBranchName: pr['source.branch.name'],
        destinationBranchName: pr['destination.branch.name'],
      })),
      issues: issues.map(issue => ({
        key: issue.key,
        summary: issue['fields.summary'],
        assignee: issue['fields.assignee.display_name'],
        status: issue['fields.status.name'],
        priority: issue['fields.priority.name'],
        issueType: issue['fields.issue_type.name'],
        url: issue['rendered_key.html'],
      })),
      commits: commits.map(commit => ({
        author: commit['author.user'].display_name,
        date: commit.date,
        hash: commit.hash,
        message: commit.message,
        url: commit['links.html.href'],
        parentHash: commit['parents.0.hash'],
      })),
    });

  return (
    <AIEventsInstrumentationProvider
      config={{
        product: 'bitbucket',
        subproduct: 'frontbucket',
        aiFeatureName: 'deploymentSummaryAiAssistant',
        proactiveGeneratedAI: 0,
        userGeneratedAI: 1,
        source: 'deploymentPreviewModal',
        customAnalyticsFire: aiEvent => {
          customAnalyticsFire(aiEvent, repositoryUuid);
        },
      }}
    >
      <AiAssistant
        buttonText={formatMessage(messages.buttonText)}
        buttonIcon={<SummarizeIcon label="" />}
        buttonSpacing="compact"
        prompt={AI_PROMPT_KEY}
        getContext={getContext}
        errorMessage={formatMessage(messages.error)}
        sentryErrorSegment="pipelines_deployment_summary_ai_assistant_exception"
        portalId={portalId}
      />
    </AIEventsInstrumentationProvider>
  );
};
